import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Box, Stack, Typography } from "@mui/material";
import * as echarts from "echarts";
import {
  buildGenericDoubleDonutInnerSeriesData,
  formatGenericDoubleDonutNameValueLabel,
  genericDoubleDonutChartConfig,
  genericDoubleDonutChartStyles,
  genericDoubleDonutDefaultInnerRadius,
  genericDoubleDonutDefaultOuterRadius,
  genericDoubleDonutEmphasisStyle,
  genericDoubleDonutInnerLabelStyle,
  genericDoubleDonutInnerSeriesLabelConfig,
  genericDoubleDonutPieCenterByLegend,
  genericDoubleDonutRightLegendStyles,
  genericDoubleDonutSliceItemStyle,
  genericDoubleDonutTooltipTextStyle,
  getGenericDoubleDonutCategoryColor,
  getGenericDoubleDonutLegendConfig,
  getGenericDoubleDonutSourceColors,
  getGenericDoubleDonutTitleSx,
  type DoubleDonutLegendPosition,
} from "./styles/genericDoubleDonutChartStyles";

export type { DoubleDonutLegendPosition };

export interface DoubleDonutCategoryItem {
  label: string | null;
  value: number;
  percentage: number;
  color?: string;
  sources:
    | {
        label: string | null;
        value: number;
        percentage: number;
        color?: string;
      }[]
    | null;
}

const NULL_LABEL = "Null";
const toLabel = (label?: string | null) => label ?? NULL_LABEL;
const getSources = (category: DoubleDonutCategoryItem) =>
  category.sources?.length
    ? category.sources
    : [{ label: null, value: category.value, percentage: category.percentage }];

export interface GenericDoubleDonutChartProps {
  title?: string;
  titleAlign?: "left" | "center" | "right";
  categories: DoubleDonutCategoryItem[];
  loading?: boolean;
  height?: number;
  minHeight?: number;
  emptyMessage?: string;
  tooltipValueFormatter?: (value: number, percent: number) => string;
  resizeTrigger?: string | number | boolean | null;
  innerRadius?: [string, string];
  outerRadius?: [string, string];
  legendPosition?: DoubleDonutLegendPosition;
}

interface RightSideLegendProps {
  categories: DoubleDonutCategoryItem[];
}

const RightSideLegend: React.FC<RightSideLegendProps> = ({ categories }) => (
  <Stack spacing={2.5} sx={genericDoubleDonutRightLegendStyles.root}>
    {categories.map((category, categoryIndex) => {
      const categoryLabel = toLabel(category.label);
      const categoryColor = getGenericDoubleDonutCategoryColor(
        categoryIndex,
        category.color,
      );
      const sources = getSources(category);
      const splitColors = getGenericDoubleDonutSourceColors(
        categoryIndex,
        sources.length,
      );

      return (
        <Box key={categoryLabel}>
          <Typography
            sx={genericDoubleDonutRightLegendStyles.categoryTitle(
              categoryColor,
            )}
          >
            {categoryLabel} Sources (Total:{" "}
            {category.value.toLocaleString("en-US")})
          </Typography>
          <Stack spacing={0.5}>
            {sources.map((source, sourceIndex) => {
              const sourceLabel = toLabel(source.label);
              const sourceColor =
                source.color ?? splitColors?.[sourceIndex] ?? categoryColor;

              return (
                <Stack
                  key={`${categoryLabel}-${sourceIndex}`}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={genericDoubleDonutRightLegendStyles.sourceRow}
                >
                  <Box
                    sx={genericDoubleDonutRightLegendStyles.sourceSwatch(
                      sourceColor,
                    )}
                  />
                  <Typography
                    sx={genericDoubleDonutRightLegendStyles.sourceLabel}
                  >
                    {sourceLabel} {":"} {source.value.toLocaleString("en-US")} (
                    {source.percentage.toFixed(2)}%)
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        </Box>
      );
    })}
  </Stack>
);

export const GenericDoubleDonutChart: React.FC<
  GenericDoubleDonutChartProps
> = ({
  title,
  titleAlign = "center",
  categories,
  loading = false,
  height = genericDoubleDonutChartConfig.defaultHeight,
  minHeight = genericDoubleDonutChartConfig.defaultMinHeight,
  emptyMessage = "No data available for the selected filters.",
  tooltipValueFormatter,
  resizeTrigger,
  innerRadius = genericDoubleDonutDefaultInnerRadius,
  outerRadius = genericDoubleDonutDefaultOuterRadius,
  legendPosition = "top",
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const resizeRafRef = useRef<number | null>(null);

  const total = useMemo(
    () => categories.reduce((sum, category) => sum + category.value, 0),
    [categories],
  );

  const hasData = total > 0;

  const innerData = useMemo(
    () =>
      categories.map((category, index) => {
        const categoryColor = getGenericDoubleDonutCategoryColor(
          index,
          category.color,
        );
        return {
          name: toLabel(category.label),
          value: category.value,
          percentage: category.percentage,
          itemStyle: { color: categoryColor },
        };
      }),
    [categories],
  );

  const outerData = useMemo(
    () =>
      categories.flatMap((category, categoryIndex) => {
        const categoryLabel = toLabel(category.label);
        const sources = getSources(category);
        const splitColors = getGenericDoubleDonutSourceColors(
          categoryIndex,
          sources.length,
        );

        return sources.map((source, sourceIndex) => {
          const sourceLabel = toLabel(source.label);
          return {
            name: `${categoryLabel} - ${sourceLabel}`,
            value: source.value,
            percentage: source.percentage,
            category: categoryLabel,
            sourceLabel,
            itemStyle: {
              color: source.color ?? splitColors?.[sourceIndex] ?? "",
            },
          };
        });
      }),
    [categories],
  );

  const scheduleResize = useCallback(() => {
    if (resizeRafRef.current !== null) {
      cancelAnimationFrame(resizeRafRef.current);
    }
    resizeRafRef.current = requestAnimationFrame(() => {
      resizeRafRef.current = null;
      chartInstance.current?.resize();
    });
  }, []);

  const defaultTooltipValueFormatter = useCallback(
    (value: number, percent: number) =>
      `${value.toLocaleString("en-US")} (${percent.toFixed(2)}%)`,
    [],
  );

  const pieCenter = genericDoubleDonutPieCenterByLegend[legendPosition];
  const showRightLegend = legendPosition === "right";

  const option = useMemo(() => {
    const formatTooltipValue =
      tooltipValueFormatter ?? defaultTooltipValueFormatter;
    const innerSeriesData = buildGenericDoubleDonutInnerSeriesData(
      innerData,
      height,
    );

    return {
      tooltip: {
        trigger: "item",
        appendToBody: true,
        confine: false,
        extraCssText: `z-index: ${genericDoubleDonutChartConfig.tooltipZIndex};`,
        textStyle: genericDoubleDonutTooltipTextStyle,
        formatter: (params: {
          name?: string;
          value?: number;
          data: {
            category?: string;
            sourceLabel?: string;
            percentage: number;
          };
        }) => {
          const value = Number(params.value ?? 0);
          const { percentage: percent, category: categoryLabel, sourceLabel } =
            params.data;
          const displayName = categoryLabel
            ? `${categoryLabel} - ${sourceLabel}`
            : (params.name ?? "");
          return `${displayName}: ${formatTooltipValue(value, percent)}`;
        },
      },
      legend: {
        ...getGenericDoubleDonutLegendConfig(showRightLegend),
        ...(showRightLegend
          ? {}
          : { data: outerData.map((item) => item.name) }),
      },
      series: [
        {
          type: "pie",
          z: 1,
          radius: outerRadius,
          center: [pieCenter.x, pieCenter.y],
          minShowLabelAngle: 0,
          label: {
            show: false,
          },
          labelLine: {
            show: false,
          },
          emphasis: genericDoubleDonutEmphasisStyle,
          itemStyle: genericDoubleDonutSliceItemStyle,
          data: outerData,
        },
        {
          type: "pie",
          z: 3,
          clip: false,
          radius: innerRadius,
          center: [pieCenter.x, pieCenter.y],
          minShowLabelAngle: 0,
          avoidLabelOverlap: true,
          label: {
            ...genericDoubleDonutInnerSeriesLabelConfig,
            formatter: (params: {
              name?: string;
              value?: number;
              data: { percentage: number };
            }) =>
              formatGenericDoubleDonutNameValueLabel(
                params.name ?? "",
                Number(params.value ?? 0),
                params.data.percentage,
              ),
            ...genericDoubleDonutInnerLabelStyle,
          },
          itemStyle: genericDoubleDonutSliceItemStyle,
          data: innerSeriesData,
        },
      ],
    };
  }, [
    defaultTooltipValueFormatter,
    height,
    innerData,
    innerRadius,
    outerData,
    outerRadius,
    pieCenter.x,
    pieCenter.y,
    showRightLegend,
    tooltipValueFormatter,
  ]);

  useEffect(() => {
    if (!hasData) {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      chartInstance.current?.dispose();
      chartInstance.current = null;
      return undefined;
    }

    const dom = chartRef.current;
    if (!dom) {
      return undefined;
    }

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(dom);
      const ro = new ResizeObserver(() => {
        scheduleResize();
      });
      ro.observe(dom);
      resizeObserverRef.current = ro;
      globalThis.addEventListener("resize", scheduleResize);
    }

    return () => {
      if (resizeRafRef.current !== null) {
        cancelAnimationFrame(resizeRafRef.current);
        resizeRafRef.current = null;
      }
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      globalThis.removeEventListener("resize", scheduleResize);
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, [hasData, scheduleResize]);

  useEffect(() => {
    if (!hasData || !chartInstance.current) {
      return;
    }
    chartInstance.current.setOption(option, {
      notMerge: true,
      lazyUpdate: true,
    });
    scheduleResize();
  }, [hasData, option, scheduleResize]);

  useEffect(() => {
    if (resizeTrigger === undefined) return;
    const timer = globalThis.setTimeout(
      () => scheduleResize(),
      genericDoubleDonutChartConfig.resizeDebounceMs,
    );
    return () => globalThis.clearTimeout(timer);
  }, [resizeTrigger, scheduleResize]);

  if (!hasData && !loading) {
    return (
      <Box>
        {title ? (
          <Typography sx={getGenericDoubleDonutTitleSx(titleAlign)}>
            {title}
          </Typography>
        ) : null}
        <Box sx={genericDoubleDonutChartStyles.emptyStateContainer}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {emptyMessage}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={genericDoubleDonutChartStyles.root}>
      {title ? (
        <Typography sx={getGenericDoubleDonutTitleSx(titleAlign)}>
          {title}
        </Typography>
      ) : null}
      <Box
        sx={genericDoubleDonutChartStyles.chartWrapper(
          minHeight,
          loading,
          hasData,
        )}
      >
        {hasData ? (
          <Box sx={genericDoubleDonutChartStyles.chartLayout}>
            <Box
              sx={genericDoubleDonutChartStyles.chartArea(
                showRightLegend,
                height,
              )}
            >
              <Box
                ref={chartRef}
                sx={genericDoubleDonutChartStyles.chartCanvas}
              />
              <Box
                sx={genericDoubleDonutChartStyles.centerLabelWrapper(
                  pieCenter.x,
                  pieCenter.y,
                )}
              >
                <Typography
                  component="span"
                  display="block"
                  sx={genericDoubleDonutChartStyles.centerLabelTitle}
                >
                  Total
                </Typography>
                <Typography
                  component="span"
                  display="block"
                  sx={genericDoubleDonutChartStyles.centerLabelValue}
                >
                  {total.toLocaleString("en-US")}
                </Typography>
              </Box>
            </Box>
            {showRightLegend ? (
              <RightSideLegend categories={categories} />
            ) : null}
          </Box>
        ) : (
          <Box sx={genericDoubleDonutChartStyles.chartPlaceholder(minHeight)} />
        )}
      </Box>
    </Box>
  );
};
